import type { ReactNode } from 'react';
import { useState } from 'react';
// OJO (gotcha histórico): `Map` de lucide se importa con alias para no
// sombrear el Map global de JS.
import { Bot, CalendarCheck, ChevronDown, Crosshair, Dumbbell, Map as MapIcon, Wheat } from 'lucide-react';
import type { Api } from '../adapters/Api';
import type { BotSettings, HuntType, ModuleKey, ResourceKey, TrainingUnitKey } from '../domain/types';
import type { RegionSeedResult } from '../domain/intelligence';
import { isValidMappingRegion } from '../domain/intelligence';
import { useI18n, type TranslationKey } from '../i18n';

// Port adaptado de apps/web/src/ui/ActionSettingsPanel.tsx (formato hosted)
// sobre el modelo de datos del descargable (modules + BotSettings del cliente).

const resources: { key: ResourceKey; label: TranslationKey }[] = [
  { key: 'hierro', label: 'settings.resources.iron' },
  { key: 'carbon', label: 'settings.resources.coal' },
  { key: 'madera', label: 'settings.resources.wood' },
  { key: 'carne', label: 'settings.resources.meat' }
];

const units: { key: TrainingUnitKey; label: TranslationKey }[] = [
  { key: 'infantry', label: 'settings.units.infantry' },
  { key: 'lancer', label: 'settings.units.lancer' },
  { key: 'marksman', label: 'settings.units.marksman' }
];

// 0.5.8: fuera la sección Game (selector de idioma del juego) — el cliente
// auto-detecta EN/ES (shell/lang.py) y avisa con modal si es otro idioma;
// game_language sigue en BotSettings para clientes antiguos (solo como pista).
// 0.6.4 (doc 17): sección 'intelligence' = mapeo de Intelligence con tokens.
type SettingsSectionKey = 'autoBot' | 'collect' | 'training' | 'hunt' | 'dailies' | 'intelligence';

const sectionIcons: Record<SettingsSectionKey, ReactNode> = {
  autoBot: <Bot size={19} />,
  collect: <Wheat size={19} />,
  training: <Dumbbell size={19} />,
  hunt: <Crosshair size={19} />,
  dailies: <CalendarCheck size={19} />,
  intelligence: <MapIcon size={19} />
};

// Celdas por pasada de mapeo (presupuesto del turno; doc 17 §5).
const INTEL_CELLS_PER_PASS_OPTIONS = [4, 6, 10] as const;

// Estado del botón "Comprobar región" (siembra self-serve vía ensure_region_seeded).
type RegionCheck =
  | { phase: 'idle' }
  | { phase: 'checking' }
  | { phase: 'done'; result: RegionSeedResult }
  | { phase: 'invalid' }
  | { phase: 'error' };

function clamp(value: number, min: number, max: number) {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, Math.round(value)));
}

function NumberStepper({
  label,
  value,
  min,
  max,
  onChange
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
}) {
  const { t } = useI18n();
  const current = clamp(value, min, max);
  return (
    <label>
      {label}
      <div className="number-stepper">
        <button type="button" aria-label={`${t('settings.down')} ${label}`} onClick={() => onChange(clamp(current - 1, min, max))}>
          -
        </button>
        <input
          min={min}
          max={max}
          type="number"
          value={current}
          onChange={(event) => onChange(clamp(Number(event.target.value), min, max))}
        />
        <button type="button" aria-label={`${t('settings.up')} ${label}`} onClick={() => onChange(clamp(current + 1, min, max))}>
          +
        </button>
      </div>
    </label>
  );
}

function CollapsibleSettingsSection({
  sectionKey,
  title,
  open,
  onToggle,
  children
}: {
  sectionKey: SettingsSectionKey;
  title: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <section className={`settings-accordion settings-accordion-${sectionKey}`}>
      <button
        type="button"
        className="settings-accordion-trigger"
        aria-label={`Configurar ${title}`}
        aria-expanded={open}
        onClick={onToggle}
      >
        <span className="settings-accordion-icon" aria-hidden="true">
          {sectionIcons[sectionKey]}
        </span>
        <span className="settings-accordion-title">{title}</span>
        <ChevronDown className="settings-accordion-chevron" size={18} aria-hidden="true" />
      </button>
      {open && <div className="settings-accordion-body">{children}</div>}
    </section>
  );
}

export function ActionSettingsPanel({
  api,
  modules,
  settings,
  onModulesChange,
  onSettingsChange
}: {
  // 0.6.4: solo para "Comprobar región" (ensure_region_seeded); el resto del
  // panel sigue siendo puro estado controlado.
  api: Api;
  modules: Record<ModuleKey, boolean>;
  settings: BotSettings;
  onModulesChange: (modules: Record<ModuleKey, boolean>) => void;
  onSettingsChange: (settings: BotSettings) => void;
}) {
  const { t } = useI18n();
  const [openSections, setOpenSections] = useState<Record<SettingsSectionKey, boolean>>({
    autoBot: true,
    collect: false,
    training: false,
    hunt: false,
    dailies: false,
    intelligence: false
  });
  const [regionCheck, setRegionCheck] = useState<RegionCheck>({ phase: 'idle' });
  function toggleSection(section: SettingsSectionKey) {
    setOpenSections((current) => ({ ...current, [section]: !current[section] }));
  }

  async function checkRegion() {
    const region = (settings.intel.region_id ?? '').trim();
    if (!isValidMappingRegion(region)) {
      setRegionCheck({ phase: 'invalid' });
      return;
    }
    setRegionCheck({ phase: 'checking' });
    try {
      const result = await api.ensureRegionSeeded(region);
      setRegionCheck({ phase: 'done', result });
    } catch {
      setRegionCheck({ phase: 'error' });
    }
  }

  function toggleResource(resource: ResourceKey) {
    const exists = settings.collect.resources.includes(resource);
    const nextResources = exists
      ? settings.collect.resources.filter((item) => item !== resource)
      : [...settings.collect.resources, resource];
    onSettingsChange({
      ...settings,
      collect: { ...settings.collect, resources: nextResources.length ? nextResources : ['hierro'] }
    });
  }

  function toggleUnit(unit: TrainingUnitKey) {
    const exists = settings.train.units.includes(unit);
    const next = exists ? settings.train.units.filter((item) => item !== unit) : [...settings.train.units, unit];
    onSettingsChange({ ...settings, train: { ...settings.train, units: next } });
  }

  return (
    <div className="settings-grid">
      <CollapsibleSettingsSection
        sectionKey="autoBot"
        title={t('dashboard.autoBot')}
        open={openSections.autoBot}
        onToggle={() => toggleSection('autoBot')}
      >
        <div className="checks">
          <label className="check">
            <input
              type="checkbox"
              checked={modules.collect}
              onChange={(event) => onModulesChange({ ...modules, collect: event.target.checked })}
            />
            {t('dashboard.collect')}
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={modules.train_troops}
              onChange={(event) => onModulesChange({ ...modules, train_troops: event.target.checked })}
            />
            {t('dashboard.trainTroops')}
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={modules.auto_hunt}
              onChange={(event) => onModulesChange({ ...modules, auto_hunt: event.target.checked })}
            />
            {t('dashboard.autoHunt')}
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={settings.extras.help}
              onChange={(event) => onSettingsChange({ ...settings, extras: { ...settings.extras, help: event.target.checked } })}
            />
            {t('settings.autoHelp')}
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={settings.extras.heal}
              onChange={(event) => onSettingsChange({ ...settings, extras: { ...settings.extras, heal: event.target.checked } })}
            />
            {t('settings.autoHeal')}
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={settings.extras.shield}
              onChange={(event) => onSettingsChange({ ...settings, extras: { ...settings.extras, shield: event.target.checked } })}
            />
            {t('settings.autoShield')}
          </label>
        </div>
      </CollapsibleSettingsSection>

      <CollapsibleSettingsSection
        sectionKey="collect"
        title={t('dashboard.collect')}
        open={openSections.collect}
        onToggle={() => toggleSection('collect')}
      >
        <div className="checks compact-checks">
          {resources.map((resource) => (
            <label className="check" key={resource.key}>
              <input
                type="checkbox"
                checked={settings.collect.resources.includes(resource.key)}
                onChange={() => toggleResource(resource.key)}
              />
              {t(resource.label)}
            </label>
          ))}
        </div>
        <div className="form-grid-2">
          <NumberStepper
            label={t('settings.level')}
            min={1}
            max={10}
            value={settings.collect.level}
            onChange={(level) => onSettingsChange({ ...settings, collect: { ...settings.collect, level } })}
          />
          <NumberStepper
            label={t('settings.marches')}
            min={1}
            max={6}
            value={settings.collect.marches}
            onChange={(marches) => onSettingsChange({ ...settings, collect: { ...settings.collect, marches } })}
          />
        </div>
      </CollapsibleSettingsSection>

      <CollapsibleSettingsSection
        sectionKey="training"
        title={t('dashboard.trainTroops')}
        open={openSections.training}
        onToggle={() => toggleSection('training')}
      >
        <div className="checks">
          {units.map((unit) => (
            <label className="check" key={unit.key}>
              <input type="checkbox" checked={settings.train.units.includes(unit.key)} onChange={() => toggleUnit(unit.key)} />
              {t(unit.label)}
            </label>
          ))}
        </div>
        <label>
          {t('settings.amount')}
          <span className="muted">{settings.train.amount_percent}%</span>
          <input
            min={5}
            max={100}
            step={5}
            type="range"
            value={settings.train.amount_percent}
            onChange={(event) =>
              onSettingsChange({ ...settings, train: { ...settings.train, amount_percent: Number(event.target.value) } })
            }
          />
        </label>
      </CollapsibleSettingsSection>

      <CollapsibleSettingsSection
        sectionKey="hunt"
        title={t('dashboard.autoHunt')}
        open={openSections.hunt}
        onToggle={() => toggleSection('hunt')}
      >
        <div className="form-grid-2">
          <label>
            {t('settings.type')}
            <select
              value={settings.hunt.type}
              onChange={(event) =>
                onSettingsChange({
                  ...settings,
                  hunt: {
                    ...settings.hunt,
                    type: event.target.value as HuntType,
                    level: event.target.value === 'terror' ? Math.min(settings.hunt.level, 20) : settings.hunt.level
                  }
                })
              }
            >
              <option value="beast">{t('settings.beasts')}</option>
              <option value="terror">{t('settings.terror')}</option>
            </select>
          </label>
          <NumberStepper
            label={t('settings.marches')}
            min={1}
            max={6}
            value={settings.hunt.marches}
            onChange={(marches) => onSettingsChange({ ...settings, hunt: { ...settings.hunt, marches } })}
          />
        </div>
        <NumberStepper
          label={t('settings.level')}
          min={1}
          max={settings.hunt.type === 'terror' ? 20 : 30}
          value={settings.hunt.level}
          onChange={(level) => onSettingsChange({ ...settings, hunt: { ...settings.hunt, level } })}
        />
        <label>
          {t('settings.timer')}
          <select
            value={settings.hunt.rally_wait_minutes}
            onChange={(event) =>
              onSettingsChange({
                ...settings,
                hunt: { ...settings.hunt, rally_wait_minutes: Number(event.target.value) as BotSettings['hunt']['rally_wait_minutes'] }
              })
            }
          >
            <option value={3}>3 min</option>
            <option value={5}>5 min</option>
            <option value={10}>10 min</option>
            <option value={15}>15 min</option>
          </select>
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={settings.hunt.cryptids}
            onChange={(event) =>
              onSettingsChange({ ...settings, hunt: { ...settings.hunt, cryptids: event.target.checked } })
            }
          />
          {t('settings.cryptids')}
        </label>
        <p className="muted">{t('settings.cryptidsHint')}</p>
      </CollapsibleSettingsSection>

      {/* Tareas diarias (B1): maestro + granular por función. El maestro activa
          la sección; los sub-toggles afinan qué se hace. El cliente lee
          extras.dailies (maestro) y extras.daily_* (granular). */}
      <CollapsibleSettingsSection
        sectionKey="dailies"
        title={t('settings.tasks')}
        open={openSections.dailies}
        onToggle={() => toggleSection('dailies')}
      >
        <label className="check">
          <input
            type="checkbox"
            checked={!!settings.extras.dailies}
            onChange={(event) => onSettingsChange({ ...settings, extras: { ...settings.extras, dailies: event.target.checked } })}
          />
          <strong>{t('settings.tasksEnable')}</strong>
        </label>
        <p className="muted">{t('settings.tasksHint')}</p>
        <div className="checks compact-checks">
          {/* daily_intel retirado del dash (2026-07-27): Intel missions queda
              como función EN DESARROLLO — el cliente la ignora en duro. */}
          {([
            ['daily_mail', 'settings.taskMail'],
            ['daily_heroes', 'settings.taskHeroes'],
            ['daily_idle', 'settings.taskIdle']
          ] as const).map(([key, labelKey]) => (
            <label className="check" key={key}>
              <input
                type="checkbox"
                disabled={!settings.extras.dailies}
                checked={settings.extras[key] !== false}
                onChange={(event) => onSettingsChange({ ...settings, extras: { ...settings.extras, [key]: event.target.checked } })}
              />
              {t(labelKey)}
            </label>
          ))}
        </div>
      </CollapsibleSettingsSection>

      {/* Mapeo de Intelligence (0.6.4, doc 17): los bots del usuario escanean la
          región elegida y ganan tokens que pagan búsquedas de Intelligence.
          El cliente lee extras.intelligence (maestro) + settings.intel.*. */}
      <CollapsibleSettingsSection
        sectionKey="intelligence"
        title={t('settings.intelMapping')}
        open={openSections.intelligence}
        onToggle={() => toggleSection('intelligence')}
      >
        <label className="check">
          <input
            type="checkbox"
            checked={!!settings.extras.intelligence}
            onChange={(event) =>
              onSettingsChange({ ...settings, extras: { ...settings.extras, intelligence: event.target.checked } })
            }
          />
          <strong>{t('settings.intelEnable')}</strong>
        </label>
        <p className="muted">{t('settings.intelHint')}</p>
        <label>
          {t('settings.intelRegion')}
          <input
            type="text"
            inputMode="numeric"
            maxLength={5}
            value={settings.intel.region_id ?? ''}
            placeholder={t('intelligence.regionPlaceholder')}
            onChange={(event) => {
              // Solo dígitos (3-5); vacío = sin configurar. Cambiar la región
              // invalida el resultado de la comprobación anterior.
              const digits = event.target.value.replace(/\D/g, '').slice(0, 5);
              setRegionCheck({ phase: 'idle' });
              onSettingsChange({ ...settings, intel: { ...settings.intel, region_id: digits || null } });
            }}
          />
        </label>
        <p className="muted">{t('settings.intelRegionHint')}</p>
        <button
          type="button"
          onClick={checkRegion}
          disabled={regionCheck.phase === 'checking' || !(settings.intel.region_id ?? '').trim()}
        >
          {regionCheck.phase === 'checking' ? t('settings.intelChecking') : t('settings.intelCheckRegion')}
        </button>
        {regionCheck.phase === 'done' && (
          <div className="notice success">
            {t(regionCheck.result.seeded ? 'settings.intelRegionSeeded' : 'settings.intelRegionReady', {
              region: regionCheck.result.regionId,
              total: regionCheck.result.totalCells.toLocaleString()
            })}
          </div>
        )}
        {regionCheck.phase === 'invalid' && <div className="notice warning">{t('settings.intelRegionInvalid')}</div>}
        {regionCheck.phase === 'error' && <div className="notice warning">{t('settings.intelRegionError')}</div>}
        <label>
          {t('settings.intelCellsPerPass')}
          <select
            value={settings.intel.cells_per_pass}
            onChange={(event) =>
              onSettingsChange({ ...settings, intel: { ...settings.intel, cells_per_pass: Number(event.target.value) } })
            }
          >
            {INTEL_CELLS_PER_PASS_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <p className="muted">{t('settings.intelCellsPerPassHint')}</p>
      </CollapsibleSettingsSection>

    </div>
  );
}
